import { api } from '../api';
import type { Asociado } from '../types';

/** Tipo del body para resolver una observación. */
export type ResolverObservacionBody =
  | { accion: 'aceptar' | 'rechazar'; nota?: string }
  | { accion: 'editar'; valor: string; nota?: string }
  | { accion: 'editar-campos'; campos: Record<string, string>; nota?: string };

/** Trae el asociado completo (todos sus campos) para editarlo al resolver. */
export async function getAsociado(id: string): Promise<Asociado> {
  const { data } = await api.get<Asociado>(`/asociados/${id}`);
  return data;
}

export interface ObservacionItem {
  legajoId: string;
  legajoNumero: string;
  padronId: string;
  empresaId: string;
  obsIndex: number;
  campo: string;
  esEnum: boolean;
  valorActual: string;
  valorPropuesto: string;
  mensaje: string;
  reportadoAt: string;
  asociadoId: string;
  asociadoNombre: string;
  asociadoDocumento: string;
  asociadoParentesco: string;
  esTitular: boolean;
}

export async function listarObservacionesAdmin(filtros: { campo?: string } = {}): Promise<ObservacionItem[]> {
  const params: Record<string, string> = {};
  if (filtros.campo) params.campo = filtros.campo;
  const { data } = await api.get<ObservacionItem[]>('/legajos/observaciones/admin', {
    params: Object.keys(params).length > 0 ? params : undefined,
  });
  return data;
}

export async function resolverObservacion(
  legajoId: string,
  obsIndex: number,
  body: ResolverObservacionBody,
): Promise<{ id: string; index: number; accion: string; observacionesPendientes: number }> {
  const { data } = await api.post(`/legajos/${legajoId}/observaciones/${obsIndex}/resolver`, body);
  return data;
}

/**
 * Variante del resolver para el flujo de ALTA MANUAL: mismo body y respuesta,
 * pero contra el endpoint del módulo alta-manual, autorizado por el permiso
 * de vista `view:alta-manual` (no por rol). El operador de altas puede ser
 * USER_MEDICUS (rol lector) con la vista habilitada por override — el
 * resolver general lo rechaza con 403 por rol.
 */
export async function resolverObservacionAltaManual(
  legajoId: string,
  obsIndex: number,
  body: ResolverObservacionBody,
): Promise<{ id: string; index: number; accion: string; observacionesPendientes: number }> {
  const { data } = await api.post(
    `/alta-manual/legajo/${legajoId}/observaciones/${obsIndex}/resolver`,
    body,
  );
  return data;
}

export async function eliminarFamiliar(
  legajoId: string,
  asociadoId: string,
  confirmDocumento: string,
): Promise<{ deleted: true; asociadoId: string; observacionesResueltas: number }> {
  const { data } = await api.delete(`/legajos/${legajoId}/familiares/${asociadoId}`, {
    data: { confirmDocumento },
  });
  return data;
}

// ─── V2: paginado + integrantes completos + agrupaciones ────────────────

export interface IntegranteLegajo {
  asociadoId: string;
  parentesco: string;
  nombres: string;
  apellSolt: string;
  documento: string;
  esTitular: boolean;
}

export interface ObservacionLegajoV2 {
  obsIndex: number;
  campo: string;
  asociadoId: string;
  asociadoNombres: string;
  asociadoApellido: string;
  asociadoParentesco: string;
  valorActual: string | null;
  valorPropuesto?: string;
  mensaje?: string;
  reportadoAt: string;
  resuelta?: boolean;
}

export interface LegajoConObservacionesV2 {
  legajoId: string;
  legajoNumero: string;
  padronId: string;
  empresaId: string;
  titularApellido: string;
  titularNombres: string;
  titularDocumento: string;
  integrantes: IntegranteLegajo[];
  observaciones: ObservacionLegajoV2[];
}

export interface AgrupacionObservaciones {
  campo: string;
  valorPropuesto: string;
  cantidad: number;
}

export interface ObservacionesPaginadasV2 {
  items: LegajoConObservacionesV2[];
  agrupaciones: AgrupacionObservaciones[];
  total: number;
  page: number;
  pageSize: number;
}

export async function listarObservacionesV2(
  opts: {
    page?: number;
    pageSize?: number;
    campo?: string;
    valorPropuesto?: string;
    q?: string;
  } = {},
): Promise<ObservacionesPaginadasV2> {
  const params: Record<string, string | number> = {
    page: opts.page ?? 1,
    pageSize: opts.pageSize ?? 20,
  };
  if (opts.campo) params.campo = opts.campo;
  if (opts.valorPropuesto !== undefined) params.valorPropuesto = opts.valorPropuesto;
  if (opts.q?.trim()) params.q = opts.q.trim();
  const { data } = await api.get<ObservacionesPaginadasV2>(
    '/legajos/observaciones-pendientes-v2',
    { params },
  );
  return data;
}
